/**
 * 登録ウィザード（3 ステップ - ICCHP 2026 §6.4 準拠）
 * Step 1: アカウント情報 (+ 教師/生徒切替 + 免責同意)
 * Step 2: 認知特性 14 項目 + screening instrument 明示バナー
 *         (triggered_axes 非空時に deep-dive 3 問×N 軸を続行。4 軸以上
 *         トリガ時は重症度降順 top-3 — utils/deepDiveAxisSelection.js)
 * Step 3: 確認画面 + 登録ボタン
 *
 * 注意:
 * - ILS 44 問は登録時点では収集しない (Profile の任意 opt-in に移動)。
 * - 教師は Step 1 → Step 3 のみ (認知特性回答なし)。
 * - 同意 checkbox は Step 1 で必須 (未チェックでは Step 2 へ進めない)。
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { authAPI } from '../utils/api';
import { saveToken } from '../utils/auth';
import { rawErrorDetail } from '../utils/errorMessage';
import { selectDeepDiveAxes } from '../utils/deepDiveAxisSelection';
import { logClientError } from '../utils/safeLog';
import FuriganaText from './FuriganaText';

// JS-side semantic alpha constant (App.css RGB triplet と組合せて 2-axis semantic 表現)
// Register.js は severityBadge / severity list bg のみ使用、cogItem / axisBadge factory 不在
const ALPHA_BG_MEDIUM = 0.125;  // severityBadge / severity list bg (20 hex byte)

const TOTAL_STEPS = 3;

// Step-label i18n keys (rendered via t()).
const STEP_LABELS = ['register.stepAccount', 'register.stepCognitive', 'register.stepConfirm'];
const TEACHER_STEP_LABELS = ['register.stepAccount', 'register.stepConfirm'];
// Grade values are stored/sent to the backend as-is (Japanese); only the DISPLAY is localized.
const GRADE_VALUE_TO_KEY = { '中学1年': 'gradeJhs1', '中学2年': 'gradeJhs2', '中学3年': 'gradeJhs3' };

// 方 = "person" (…方が/…方は) → かた. The universal engine reads a 名詞-非自立 方 as ほう
// (correct over stimulus 大きい方=ほう), so the auth link supplies its own reading (方案C, Codex R16).
const PERSON_KATA_FURIGANA = { 方: 'かた' };
const PARTICIPANT_CODE_RE = /^[A-Z][0-9]{2}$/;

function normalizeParticipantCode(value) {
  return String(value || '').normalize('NFKC').trim().toUpperCase();
}

function Register() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(() => searchParams.get('role') === 'teacher' ? 'teacher' : 'student');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    grade_level: '中学2年',
  });
  // >=13 要件は生年月日を収集せず、本人の明示確認だけを必須化する。
  const [ageRequirementAcked, setAgeRequirementAcked] = useState(false);
  // 認知特性 14 項目の回答 {45: 2, 46: 1, ...}
  const [cogAnswers, setCogAnswers] = useState({});
  // ICCHP 2026 §6.4: スクリーニング免責同意
  const [disclaimerAcked, setDisclaimerAcked] = useState(false);
  // deep-dive 質問群 (triggered_axes が非空のとき動的に取得)
  const [deepDiveQuestions, setDeepDiveQuestions] = useState([]);
  const [deepDiveAnswers, setDeepDiveAnswers] = useState({});
  const [showDeepDive, setShowDeepDive] = useState(false);
  // アカウント登録 (authAPI.register) が既に成功したか。deep-dive 送信が
  // 失敗した後の再試行で register を再実行しないためのフラグ
  // (再実行すると重複メール 400 になる)。
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  // 登録時の core14 回答スナップショット (Codex round-2 P1)。登録成功後は
  // サーバに刻まれたスコアが正 — core14 の草稿再編集は許さない (deep-dive
  // ページからの「戻る」をブロック + 軸選定は本スナップショット固定)。
  // 編集を許すと「無トリガに改答 → deep-dive 送信 skip → 静默未完成」の
  // 経路が復活する。
  const registeredCogAnswersRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isTeacher = role === 'teacher';
  const totalSteps = isTeacher ? 2 : TOTAL_STEPS;
  const stepLabels = isTeacher ? TEACHER_STEP_LABELS : STEP_LABELS;

  // CORE14 質問データ (backend から mode=core14 で取得)
  const [cogQuestions, setCogQuestions] = useState([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [screeningDisclaimer, setScreeningDisclaimer] = useState('');

  // CORE14 質問データ取得
  useEffect(() => {
    if (isTeacher) {
      setQuestionsLoaded(true);
      return;
    }
    const fetchQuestions = async () => {
      try {
        const resp = await api.get('/api/questionnaire?mode=core14');
        setCogQuestions(resp.data.cognitive_questions || []);
        setScreeningDisclaimer(resp.data.screening_disclaimer || '');
        setQuestionsLoaded(true);
      } catch (err) {
        logClientError('Failed to load questionnaire:', err);
        setError('register.error.loadQuestions');
      }
    };
    fetchQuestions();
  }, [isTeacher]);

  const handleChange = (e) => {
    const { name } = e.target;
    const value = name === 'username' && !isTeacher
      ? normalizeParticipantCode(e.target.value)
      : e.target.value;
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleCogAnswer = useCallback((questionId, value) => {
    setCogAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const handleDeepDiveAnswer = useCallback((questionId, value) => {
    setDeepDiveAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  // Step 2 完了判定: 14 項目 + (deep-dive 表示中なら deep-dive 全問)
  const isStep2Complete = () => {
    if (cogQuestions.length === 0) return false;
    const allCoreAnswered = cogQuestions.every(q => cogAnswers[q.id] !== undefined);
    if (!allCoreAnswered) return false;
    if (showDeepDive) {
      return deepDiveQuestions.every(q => deepDiveAnswers[q.id] !== undefined);
    }
    return true;
  };

  const isStepComplete = () => {
    if (step === 1) {
      return true; // form submission 経由で進行
    }
    if (isTeacher) return true;
    if (step === 2) return isStep2Complete();
    return true; // step 3 confirmation
  };

  // CORE14 のクライアント側スコアリング + deep-dive 軸選定は
  // utils/deepDiveAxisSelection.js (selectDeepDiveAxes) に集約 —
  // 重症度降順 (同点は CORE_AXES 宣言順) で最大 3 軸。backend
  // (CognitiveDifficultyScorer.select_deep_dive_axes) と鏡像で、
  // CI sync guard: backend/tests/test_deep_dive_axis_selection_frontend_sync.py

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('register.error.invalidEmail');
      return;
    }
    if (!formData.username || formData.username.trim().length === 0) {
      setError('register.error.usernameRequired');
      return;
    }
    if (!isTeacher && !PARTICIPANT_CODE_RE.test(normalizeParticipantCode(formData.username))) {
      setError('register.error.participantCodeInvalid');
      return;
    }
    // Caught at step 1, not at the final confirm: on 2026-08-22 a research ID was
    // refused only after every screening answer had been filled in, and switching
    // the role to 先生 was what made the error go away — which silently skipped
    // the participant-code contract for the whole session.
    if (isTeacher && PARTICIPANT_CODE_RE.test(normalizeParticipantCode(formData.username))) {
      setError('register.error.teacherReservedCode');
      return;
    }
    if (!formData.password) {
      setError('register.error.passwordRequired');
      return;
    }
    if (formData.password.length < 6) {
      setError('register.error.passwordTooShort');
      return;
    }
    if (!formData.confirmPassword) {
      setError('register.error.confirmRequired');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('register.error.passwordMismatch');
      return;
    }
    if (!isTeacher && !disclaimerAcked) {
      setError('register.error.disclaimerRequired');
      return;
    }
    if (!isTeacher && !ageRequirementAcked) {
      setError('register.error.ageConfirmationRequired');
      return;
    }
    setStep(isTeacher ? 2 : 2);
    setError('');
  };

  // Step 2: 14 問完了 → triggered_axes チェック → 必要なら deep-dive 展開 → Step 3 へ
  const handleStep2Next = async () => {
    if (!isStep2Complete()) {
      setError('register.error.answerAll');
      return;
    }

    // すでに deep-dive を表示済みであれば Step 3 へ
    if (showDeepDive) {
      setStep(3);
      setError('');
      window.scrollTo(0, 0);
      return;
    }

    // triggered 軸をクライアント側で選定: 重症度降順 top-3 (同点は
    // CORE_AXES 宣言順、cap 込み — utils/deepDiveAxisSelection.js 参照)。
    // これは推奨選択。backend /api/auth/cognitive-deep-dive は triggered
    // 軸の ≤3 軸子集なら何でも受理する (子集容忍、混合版本安全)。
    // 登録済みなら軸選定は登録時スナップショットに固定 (Codex round-2 P1
    // 防御 — UI 上は core14 編集経路自体をブロック済みだが、選定が草稿に
    // 追随しないことをここでも保証する)。
    const triggeredCapped = selectDeepDiveAxes(
      registrationCompleted && registeredCogAnswersRef.current
        ? registeredCogAnswersRef.current
        : cogAnswers
    );
    if (triggeredCapped.length === 0) {
      // トリガなし → そのまま Step 3
      setStep(3);
      setError('');
      window.scrollTo(0, 0);
      return;
    }

    // deep-dive 質問取得
    try {
      const axesParam = encodeURIComponent(triggeredCapped.join(','));
      const resp = await api.get(`/api/questionnaire?mode=trigger_axis&axes=${axesParam}`);
      const questions = resp.data.cognitive_questions || [];
      if (questions.length > 0) {
        setDeepDiveQuestions(questions);
        setShowDeepDive(true);
        setError('');
        window.scrollTo(0, 0);
        return;
      }
    } catch (err) {
      logClientError('Failed to load deep-dive questions:', err);
    }
    // フォールバック: 取得失敗時は Step 3 へ
    setStep(3);
    setError('');
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    if (step === 2 && !isTeacher) {
      handleStep2Next();
      return;
    }
    if (!isStepComplete()) {
      setError('register.error.answerAll');
      return;
    }
    setError('');
    setStep(s => Math.min(s + 1, totalSteps));
    window.scrollTo(0, 0);
  };

  const handlePrev = () => {
    setError('');
    if (step === 2 && showDeepDive) {
      // 登録成功後は core14 編集に戻さない (Codex round-2 P1): サーバに
      // 刻まれた登録時スコアが正であり、草稿を無トリガに改答して deep-dive
      // 送信を skip する「静默未完成」経路を塞ぐ。この状態では「戻る」
      // ボタン自体を非表示にしているが、防御として no-op を保証する。
      if (registrationCompleted) return;
      // deep-dive 表示中に戻る → core14 編集に戻す
      setShowDeepDive(false);
      setDeepDiveAnswers({});
      window.scrollTo(0, 0);
      return;
    }
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // 二相 submit: (1) アカウント登録 → (2) deep-dive 送信。
      // deep-dive 失敗後の再試行では (1) を skip して (2) のみ再送する
      // (register を再実行すると重複メール 400 になるため)。
      if (!registrationCompleted) {
        const registerData = isTeacher
          ? {
              email: formData.email,
              username: formData.username,
              password: formData.password,
              grade_level: '教師',
              role: 'teacher',
              screening_disclaimer_acknowledged: false, // 教師は免責不要
            }
          : {
              email: formData.email,
              username: normalizeParticipantCode(formData.username),
              password: formData.password,
              grade_level: formData.grade_level,
              cognitive_answers: cogAnswers,
              screening_disclaimer_acknowledged: disclaimerAcked,
              age_requirement_acknowledged: ageRequirementAcked,
            };

        const response = await authAPI.register(registerData);
        const { access_token } = response.data;
        saveToken(access_token);
        setRegistrationCompleted(true);
        // core14 回答を登録時スナップショットとして凍結 (以降の deep-dive
        // 軸選定はこのスナップショット基準 — Codex round-2 P1)。
        registeredCogAnswersRef.current = { ...cogAnswers };
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // deep-dive 回答があれば追加送信 (生徒のみ)。
      // 失敗は握りつぶさない: 旧挙動は silent-skip で Dashboard へ遷移し、
      // questionnaire_completed=False のまま回復 UI が無かった (Codex P1
      // 2026-07-10)。可視エラーを出して留まり、同じ「登録完了」ボタンで
      // deep-dive 送信だけを再試行できる (「戻る」で回答修正も可能)。
      if (!isTeacher && Object.keys(deepDiveAnswers).length > 0) {
        try {
          await api.post('/api/auth/cognitive-deep-dive', {
            deep_dive_answers: deepDiveAnswers,
          });
        } catch (e) {
          // 一次性 gate (backend 409 already_completed): 別タブ/応答喪失後の
          // 再試行等で「既に完了済み」なら、再送し続けても 409 のままなので
          // 完了と同義に扱い先へ進む (無限再試行ループ防止)。
          if (e?.response?.data?.detail?.code === 'already_completed') {
            navigate('/dashboard');
            return;
          }
          logClientError('Deep-dive submission failed:', e);
          setError('register.error.deepDiveSubmitFailed');
          return;
        }
      }

      if (isTeacher) {
        localStorage.setItem('graph_tutor_role', 'teacher');
      }
      navigate('/dashboard');
    } catch (err) {
      logClientError('Register error:', err);
      // Backend `detail` is Japanese-only (Phase 7 backend codes not yet in place).
      // The age-confirmation 400 is substring-matched via rawErrorDetail() — a
      // NON-rendered, classification-only read (see its doc) — and mapped to the
      // localized confirmation key. Research-ID policy failures are likewise
      // mapped to the dedicated field message; all other failures
      // (email/username taken, disclaimer, etc.) keep the generic fallback.
      const detail = rawErrorDetail(err);
      if (isTeacher && detail.includes('管理者の承認')) {
        setError('register.error.teacherNotApproved');
      } else if (isTeacher && detail.includes('研究ID')) {
        setError('register.error.teacherReservedCode');
      } else if (!isTeacher && detail.includes('まだ使えません')) {
        // Well-formed but not issued. Kept distinct from the shape message: the
        // participant's code is typed correctly, so telling them the format is
        // wrong makes them re-type a correct value and look for a way around it.
        setError('register.error.participantCodeUnissued');
      } else if (!isTeacher && detail.includes('研究ID')) {
        setError('register.error.participantCodeInvalid');
      } else if (!isTeacher && detail.includes('13歳以上')) {
        setError('register.error.ageConfirmationRequired');
      } else {
        setError('register.error.registerFailed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Compute summary data for Step 3 (生徒)
  const computeSummary = () => {
    const cogLabels = {
      working_memory: t('register.cogLabel.working_memory'),
      visuospatial: t('register.cogLabel.visuospatial'),
      crossmodal: t('register.cogLabel.crossmodal'),
      load_vulnerability: t('register.cogLabel.load_vulnerability'),
      executive_inhibition: t('register.cogLabel.executive_inhibition'),
      language_semantic: t('register.cogLabel.language_semantic'),
      math_anxiety: t('register.cogLabel.math_anxiety'),
      graph_literacy: t('register.cogLabel.graph_literacy'),
    };
    const severityLabels = [
      t('register.severity.none'),
      t('register.severity.mild'),
      t('register.severity.moderate'),
      t('register.severity.severe'),
    ];
    // CORE14: 各タイプ 2 問 (modulator は 1 問)
    const cogTypes = ['working_memory', 'visuospatial', 'crossmodal', 'load_vulnerability', 'executive_inhibition', 'language_semantic', 'math_anxiety', 'graph_literacy'];
    const cogSummary = {};
    const qPerType = {};
    cogQuestions.forEach(q => {
      if (!qPerType[q.difficulty_type]) qPerType[q.difficulty_type] = [];
      qPerType[q.difficulty_type].push(q.id);
    });

    for (const type of cogTypes) {
      const ids = qPerType[type] || [];
      // deep-dive 回答もあれば組み込む (UI 表示の精度向上)
      const deepIds = deepDiveQuestions
        .filter(q => q.difficulty_type === type)
        .map(q => q.id);
      const allIds = [...ids, ...deepIds];
      const vals = allIds.map(id => cogAnswers[id] ?? deepDiveAnswers[id] ?? null).filter(v => v !== null);
      const avg = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
      cogSummary[type] = {
        label: cogLabels[type],
        score: avg,
        severity: severityLabels[avg],
      };
    }

    return { cogSummary };
  };

  // ==================== Render ====================

  const handleRoleChange = (newRole) => {
    if (newRole !== role) {
      setRole(newRole);
      setStep(1);
      setError('');
      setShowDeepDive(false);
      setDeepDiveAnswers({});
      setAgeRequirementAcked(false);
      // 役割を切り替えたら submit 二相状態もリセット (別アカウントの新規
      // 登録になるため、前アカウントの register 済みフラグ/スナップショット
      // を引き継がない)。
      setRegistrationCompleted(false);
      registeredCogAnswersRef.current = null;
    }
  };

  const progressPercent = ((step - 1) / Math.max(totalSteps - 1, 1)) * 100;

  return (
    <div className="min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto" style={{ maxWidth: (step === 1 || step === totalSteps) ? '700px' : '940px', transition: 'max-width 0.3s ease' }}>
        {/* Header */}
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-xl"
            style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}
          >
            {t('register.logoGlyph')}
          </div>
          <h1 className="text-base font-bold tracking-wide" style={{ color: 'var(--text)' }}>
            Graph Tutor AI
          </h1>
          <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={isTeacher ? t('register.teacherTitle') : t('register.studentTitle')} />
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="mb-2 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
            <span>{t('register.stepProgress', { step, total: totalSteps })}</span>
            <span><FuriganaText text={t(stepLabels[step - 1])} /></span>
          </div>
          <div className="h-2 w-full rounded-full" style={{ background: 'var(--bg-muted)' }}>
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%`, background: 'var(--primary)' }}
            />
          </div>
          {/* Step dots */}
          <div className="mt-2 flex justify-between px-0.5">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className="h-2 w-2 rounded-full transition-colors"
                style={{
                  background: i + 1 <= step ? 'var(--primary)' : 'var(--border)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Main Card */}
        <div
          className="rounded-[24px] p-6 sm:p-8 backdrop-blur"
          style={{
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {error && (
            <div
              role="alert"
              className="mb-5 rounded-2xl p-4 text-sm"
              style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              <FuriganaText text={t(error)} />
            </div>
          )}

          {/* Step 1: Account Info */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} noValidate className="space-y-4">
              <h2 className="mb-4 text-base font-bold" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('register.accountInfoTitle')} />
              </h2>

              {/* Role selector tabs */}
              <div
                className="flex rounded-xl p-1"
                style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)' }}
              >
                <button
                  type="button"
                  onClick={() => handleRoleChange('student')}
                  className="flex-1 rounded-lg py-2 text-xs font-semibold transition-all"
                  style={{
                    background: !isTeacher ? 'var(--bg-surface)' : 'transparent',
                    color: !isTeacher ? 'var(--primary)' : 'var(--text-muted)',
                    boxShadow: !isTeacher ? 'var(--shadow-sm)' : 'none',
                  }}
                >
                  <FuriganaText text={t('register.registerAsStudent')} />
                </button>
                <button
                  type="button"
                  onClick={() => handleRoleChange('teacher')}
                  className="flex-1 rounded-lg py-2 text-xs font-semibold transition-all"
                  style={{
                    background: isTeacher ? 'var(--bg-surface)' : 'transparent',
                    color: isTeacher ? 'var(--primary)' : 'var(--text-muted)',
                    boxShadow: isTeacher ? 'var(--shadow-sm)' : 'none',
                  }}
                >
                  <FuriganaText text={t('register.registerAsTeacher')} />
                </button>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--text)' }}>
                  {t('auth.login.emailLabel')}
                </label>
                <input
                  type="text" name="email" value={formData.email}
                  onChange={handleChange} required
                  className="input-field rounded-2xl" placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="register-username" className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--text)' }}>
                  <FuriganaText text={t(isTeacher ? 'register.usernameLabel' : 'register.participantCodeLabel')} />
                </label>
                <input
                  type="text" id="register-username" name="username" value={formData.username}
                  onChange={handleChange} required
                  maxLength={isTeacher ? undefined : 3}
                  autoCapitalize={isTeacher ? undefined : 'characters'}
                  className="input-field rounded-2xl" placeholder={isTeacher ? 'username' : 'A01'}
                />
                {!isTeacher && (
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('register.participantCodeHint')} />
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--text)' }}>
                  <FuriganaText text={t('register.passwordLabel6')} />
                </label>
                <input
                  type="password" name="password" value={formData.password}
                  onChange={handleChange} required minLength="6"
                  className="input-field rounded-2xl" placeholder="********"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--text)' }}>
                  <FuriganaText text={t('register.confirmLabel')} />
                </label>
                <input
                  type="password" name="confirmPassword" value={formData.confirmPassword}
                  onChange={handleChange} required minLength="6"
                  className="input-field rounded-2xl" placeholder="********"
                />
              </div>

              {!isTeacher && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold" style={{ color: 'var(--text)' }}>
                    <FuriganaText text={t('register.gradeLabel')} />
                  </label>
                  <select
                    name="grade_level" value={formData.grade_level}
                    onChange={handleChange} className="input-field rounded-2xl"
                  >
                    <option value="中学1年">{t('register.gradeJhs1')}</option>
                    <option value="中学2年">{t('register.gradeJhs2')}</option>
                    <option value="中学3年">{t('register.gradeJhs3')}</option>
                  </select>
                </div>
              )}

              {/* >=13 歳確認 (研究計画 §4.2+§4.4, 生徒のみ)。DOB は収集・送信しない。 */}
              {!isTeacher && (
                <div
                  className="rounded-2xl p-4 text-xs"
                  style={{
                    border: '1px solid var(--border)',
                    background: 'var(--primary-bg)',
                    color: 'var(--text)',
                  }}
                >
                  <label htmlFor="register-age-confirmation" className="flex items-start gap-2 cursor-pointer font-semibold">
                    <input
                      type="checkbox"
                      id="register-age-confirmation"
                      checked={ageRequirementAcked}
                      onChange={(e) => {
                        setAgeRequirementAcked(e.target.checked);
                        if (e.target.checked) setError('');
                      }}
                      className="mt-0.5"
                    />
                    <span style={{ color: 'var(--text)' }}>
                      <FuriganaText text={t('register.ageConfirmationLabel')} />
                    </span>
                  </label>
                  <p className="mt-2 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('register.ageConfirmationPrivacyNote')} />
                  </p>
                </div>
              )}

              {/* Screening Disclaimer + 同意チェック (生徒のみ, ICCHP 2026 §6.4 必須) */}
              {!isTeacher && (
                <div
                  className="rounded-2xl p-4 text-xs"
                  style={{
                    border: '1px solid var(--border)',
                    background: 'var(--warm-bg)',
                    color: 'var(--text)',
                  }}
                >
                  <div className="mb-2 font-semibold" style={{ color: 'var(--primary-strong)' }}>
                    <FuriganaText text={t('register.disclaimerHeading')} />
                  </div>
                  <p className="mb-3 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={(i18n.language === 'ja' && screeningDisclaimer) || t('register.disclaimerFallback')} />
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={disclaimerAcked}
                      onChange={(e) => {
                        setDisclaimerAcked(e.target.checked);
                        if (e.target.checked) setError('');
                      }}
                      className="mt-0.5"
                    />
                    <span style={{ color: 'var(--text)' }}>
                      <FuriganaText text={t('register.disclaimerConsent')} />
                    </span>
                  </label>
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  type="submit"
                  className="btn-primary rounded-full py-2 px-8 text-xs transition-transform hover:scale-[1.02]"
                >
                  <FuriganaText text={t('register.next')} />
                </button>
              </div>

              <div className="mt-3 text-center">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText overrides={PERSON_KATA_FURIGANA}>
                    {t('register.haveAccount')}{' '}
                    <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--accent)' }}>
                      <FuriganaText>{t('auth.login.title')}</FuriganaText>
                    </Link>
                  </FuriganaText>
                </p>
              </div>
            </form>
          )}

          {/* Step 2: Cognitive Difficulty 14 項目 (+ deep-dive on trigger) — 生徒のみ */}
          {!isTeacher && step === 2 && questionsLoaded && !showDeepDive && (
            <CognitiveCorePage
              questions={cogQuestions}
              answers={cogAnswers}
              onAnswer={handleCogAnswer}
              screeningDisclaimer={screeningDisclaimer}
            />
          )}

          {!isTeacher && step === 2 && showDeepDive && (
            <CognitiveDeepDivePage
              questions={deepDiveQuestions}
              answers={deepDiveAnswers}
              onAnswer={handleDeepDiveAnswer}
            />
          )}

          {/* Step 3: Summary — 生徒 */}
          {!isTeacher && step === 3 && (
            <SummaryPage
              formData={formData}
              summary={computeSummary()}
            />
          )}

          {/* Step 2 (= teacher confirm step) */}
          {isTeacher && step === 2 && (
            <div>
              <h2 className="mb-4 text-base font-bold" style={{ color: 'var(--text)' }}>
                <FuriganaText text={t('register.confirmTitle')} />
              </h2>
              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
                <h3 className="text-xs font-bold mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('register.accountInfo')} /></h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryRegType')} /></span>
                    <span className="rounded-full px-3 py-0.5 text-xs font-semibold" style={{ background: 'var(--primary-bg)', color: 'var(--primary)' }}><FuriganaText text={t('register.summaryTeacher')} /></span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>{t('register.summaryEmail')}</span>
                    <span style={{ color: 'var(--text)' }}>{formData.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryUsername')} /></span>
                    {/* a participant-entered kanji username is an unverified proper name — never furigana-guess
                        it (小鳥遊 → 小鳥(ことり)遊 is wrong); render bare like BARE_SURFACES (Codex R19 #2) */}
                    <span style={{ color: 'var(--text)' }}>{formData.username}</span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('register.teacherNote')} />
              </p>
            </div>
          )}

          {/* Navigation buttons (steps 2+) */}
          {step >= 2 && (
            <div className="mt-6 flex justify-center gap-3">
              {/* 登録成功後の deep-dive ページでは「戻る」を出さない —
                  core14 は登録時スナップショットで確定済み (Codex round-2
                  P1)。Step 3 からの「戻る」(deep-dive 回答の見直し) は可。 */}
              {!(registrationCompleted && step === 2 && showDeepDive) && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="btn-secondary rounded-full py-2 px-8 text-xs"
                >
                  <FuriganaText text={t('register.back')} />
                </button>
              )}
              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary rounded-full py-2 px-8 text-xs transition-transform hover:scale-[1.02]"
                >
                  <FuriganaText text={t('register.next')} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary rounded-full py-2 px-8 text-xs transition-transform hover:scale-[1.02] disabled:scale-100 disabled:opacity-60"
                >
                  <FuriganaText>{loading ? t('register.registering') : t('register.complete')}</FuriganaText>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== Cognitive Core 14 Page ====================

function CognitiveCorePage({ questions, answers, onAnswer, screeningDisclaimer }) {
  const { t, i18n } = useTranslation();
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
          <FuriganaText text={t('register.cogCoreTitle')} />
        </h2>
        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          <FuriganaText text={t('register.cogCoreSubtitle')} />
        </p>
      </div>

      {/* スクリーニング明示バナー */}
      <div
        className="mb-5 rounded-2xl p-3 text-xs"
        style={{
          border: '1px solid var(--accent)',
          background: 'rgba(132,63,31,0.06)',
          color: 'var(--text)',
        }}
      >
        <strong style={{ color: 'var(--accent)' }}><FuriganaText text={t('register.screeningLabel')} /></strong>{' '}
        <FuriganaText text={(i18n.language === 'ja' && screeningDisclaimer) || t('register.screeningFallback')} />
      </div>

      <div className="space-y-4">
        {questions.map((q, idx) => (
          <CogQuestionCard
            key={q.id}
            question={q}
            index={idx}
            selected={answers[q.id]}
            onSelect={(val) => onAnswer(q.id, val)}
          />
        ))}
      </div>
    </div>
  );
}


// ==================== Cognitive Deep-Dive Page ====================

function CognitiveDeepDivePage({ questions, answers, onAnswer }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
          <FuriganaText text={t('register.deepDiveTitle')} />
        </h2>
        <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          <FuriganaText text={t('register.deepDiveSubtitle')} />
        </p>
      </div>

      <div
        className="mb-5 rounded-2xl p-3 text-xs"
        style={{
          border: '1px solid var(--primary)',
          background: 'var(--primary-bg)',
          color: 'var(--text)',
        }}
      >
        <strong style={{ color: 'var(--primary-strong)' }}><FuriganaText text={t('register.deepDiveCount', { count: questions.length })} /></strong>{' '}
        <FuriganaText text={t('register.deepDiveNote')} />
      </div>

      <div className="space-y-4">
        {questions.map((q, idx) => (
          <CogQuestionCard
            key={q.id}
            question={q}
            index={idx}
            selected={answers[q.id]}
            onSelect={(val) => onAnswer(q.id, val)}
          />
        ))}
      </div>
    </div>
  );
}


function CogQuestionCard({ question, index, selected, onSelect }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        border: `1px solid ${selected !== undefined ? 'var(--primary)' : 'var(--border)'}`,
        background: selected !== undefined ? 'var(--primary-bg)' : 'var(--bg-surface)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <p className="mb-3 text-sm font-medium" style={{ color: 'var(--text)' }}>
        <span className="mr-2" style={{ color: 'var(--text-muted)' }}>Q{index + 1}.</span>
        {question.question}
      </p>
      <div className="space-y-1.5">
        {question.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className="w-full rounded-xl px-3 py-2.5 text-sm text-left transition-all"
            style={{
              border: `2px solid ${selected === opt.value ? 'var(--primary)' : 'var(--border)'}`,
              background: selected === opt.value ? 'var(--primary)' : 'transparent',
              color: selected === opt.value ? 'var(--text-on-primary)' : 'var(--text)',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}


// ==================== Summary Page ====================

function SummaryPage({ formData, summary }) {
  const { t } = useTranslation();
  const { cogSummary } = summary;
  // Index ordering MUST match: success/warning/accent/danger
  const severityColors    = ['var(--success)', 'var(--warning)', 'var(--accent)', 'var(--danger)'];
  const severityColorsRgb = ['var(--success-rgb)', 'var(--warning-rgb)', 'var(--accent-rgb)', 'var(--danger-rgb)'];

  return (
    <div>
      <h2 className="mb-4 text-base font-bold" style={{ color: 'var(--text)' }}>
        <FuriganaText text={t('register.confirmTitle')} />
      </h2>

      {/* Account Info */}
      <div className="mb-5 rounded-2xl p-4" style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
        <h3 className="text-xs font-bold mb-2" style={{ color: 'var(--text)' }}><FuriganaText text={t('register.accountInfo')} /></h3>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-muted)' }}>{t('register.summaryEmail')}</span>
            <span style={{ color: 'var(--text)' }}>{formData.email}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryParticipantCode')} /></span>
            {/* unverified proper name → bare (never-guess), Codex R19 #2 */}
            <span style={{ color: 'var(--text)' }}>{formData.username}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.summaryGrade')} /></span>
            <span style={{ color: 'var(--text)' }}><FuriganaText text={t('register.' + (GRADE_VALUE_TO_KEY[formData.grade_level] || 'gradeJhs2'))} /></span>
          </div>
        </div>
      </div>

      {/* Cognitive Difficulty */}
      <div className="rounded-2xl p-4" style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
        <h3 className="text-xs font-bold mb-3" style={{ color: 'var(--text)' }}><FuriganaText text={t('register.cogResultTitle')} /></h3>
        <div className="space-y-2">
          {Object.entries(cogSummary)
            .filter(([type]) => !['math_anxiety', 'graph_literacy'].includes(type))
            .map(([type, s]) => (
            <div key={type} className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--text)' }}><FuriganaText text={s.label} /></span>
              <span
                className="rounded-full px-3 py-0.5 text-xs font-semibold"
                style={{
                  background: `rgba(${severityColorsRgb[s.score]}, ${ALPHA_BG_MEDIUM})`,
                  color: severityColors[s.score],
                }}
              >
                <FuriganaText text={s.severity} />
              </span>
            </div>
          ))}
        </div>

        {/* Modifiers */}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: '12px', paddingTop: '12px' }}>
          <p className="text-xs font-bold mb-2" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('register.modifiers')} /></p>
          <div className="space-y-2">
            {Object.entries(cogSummary)
              .filter(([type]) => ['math_anxiety', 'graph_literacy'].includes(type))
              .map(([type, s]) => (
              <div key={type} className="flex items-center justify-between text-sm">
                <span style={{ color: 'var(--text)' }}><FuriganaText text={s.label} /></span>
                <span
                  className="rounded-full px-3 py-0.5 text-xs font-semibold"
                  style={{
                    background: `rgba(${severityColorsRgb[s.score]}, ${ALPHA_BG_MEDIUM})`,
                    color: severityColors[s.score],
                  }}
                >
                  <FuriganaText text={s.severity} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={t('register.afterNote')} />
      </p>
    </div>
  );
}


export default Register;
